import React, { useEffect } from 'react'
import './style/insights-filter-builder.scss'
import { getClassNames } from '../../../_utils/classnames'
import { BetterSelect } from '../../../components/better-select/better-select'
import clone from 'clone'
import { Loading3QuartersOutlined } from '@ant-design/icons'
import { Select } from 'antd'
import { UserDto } from '../../../dtos/user'
import { UserService } from '../../../services/index'
import { useService } from '../../../hooks/use-service'

type FilterValue = {
    $mode: string
    field: string
    operator: string
    value: number[]
}

interface IOptionsType {
    value: number
    label: string
}

interface ICreatedByFilterProps {
    value: FilterValue
    onChange: (filterValue: FilterValue) => void
}

export function CreatedByFilterBuilder(props: ICreatedByFilterProps) {
    const userService = useService(UserService)

    const [userIds, setUserIds] = React.useState<number[]>()
    const [loading, setLoading] = React.useState<boolean>(false)
    const [users, setUsers] = React.useState(Array<IOptionsType>)

    const currentValue = (): any => {
        const propsValue: any = props.value || {}
        return clone({
            operator: propsValue.operator || 'IN',
            value: propsValue.value ?? userIds,
        })
    }

    const currentOperatorDisplay = (): string => {
        switch (currentValue().operator) {
            case 'IN':
                return 'is'
            case 'NOT_IN':
                return 'is not'
            default:
                return 'is'
        }
    }

    const currentValueDisplay = (): string => {
        let display: string = ''
        let createdByUsers: string[] = []
        createdByUsers = users.filter((s) => userIds?.includes(s.value)).map((s) => s.label)

        if (createdByUsers.length < 1) {
            display = 'any user'
        } else {
            if (createdByUsers.length <= 2) {
                display = createdByUsers.join(' or ')
            } else {
                const lastValue = createdByUsers.pop()
                display = createdByUsers.join(', ') + `, or ${lastValue}`
            }
        }

        return display
    }

    const createdByOperatorOptions = (): Array<{ value: string; label: string }> => {
        const options = [
            { value: 'IN', label: 'is' },
            { value: 'NOT_IN', label: 'is not' },
        ]

        return options
    }

    const handleOpChange = (operator: string) => {
        props.onChange({
            ...props.value,
            operator,
        })
    }

    const handleValueChange = (selectedUserIds: number[]) => {
        setUserIds(selectedUserIds)

        props.onChange({
            ...currentValue(),
            value: selectedUserIds,
        })
    }

    const loadDomainUsers = async () => {
        setLoading(true)

        const domainUsers = await userService.fetchUsersForCurrentDomain({
            query: {
                pagination: 0,
            },
        })

        if (domainUsers) {
            const multiSelectFormat = domainUsers
                .filter((u) => !u.isInternalUser) // this view should never display internal users
                .map((u: UserDto) => ({
                    value: u.id,
                    label: u.name,
                }))

            let newValue: number[] = []

            if (props.value) {
                newValue = props.value.value
            }

            setUsers(multiSelectFormat)
            setUserIds(newValue)
        }

        setLoading(false)
    }

    useEffect(() => {
        loadDomainUsers()
    }, [])

    return (
        <div className={getClassNames('filter', 'insights-filter-input')}>
            <div className={getClassNames('filter-wrapper')}>
                <div className={getClassNames('filter-display')}>
                    <div className="filter-op">{currentOperatorDisplay()}</div>
                    <div className="filter-val">
                        <div id="borderDisplay"></div>
                        {loading ? (
                            <Loading3QuartersOutlined
                                size={1}
                                spin={true}
                                style={{ display: 'inline-flex', marginTop: '2px' }}
                            />
                        ) : (
                            <span>{currentValueDisplay()}</span>
                        )}
                    </div>
                </div>
                <div className={getClassNames('filter-editor')}>
                    <div className="filter-op">
                        <BetterSelect
                            size="small"
                            disableSearch={true}
                            dropdownClassName="filter-op-dropdown"
                            options={createdByOperatorOptions()}
                            defaultValue={['IN']}
                            value={currentValue().operator}
                            onChange={handleOpChange}
                        />
                    </div>
                    <div className="filter-val">
                        <div id="borderEdit"></div>
                        <Select
                            mode="multiple"
                            size="small"
                            className="sw-v2-created_by-filter"
                            options={users || []}
                            optionFilterProp="label"
                            onChange={handleValueChange}
                            placeholder={
                                loading ? (
                                    <Loading3QuartersOutlined
                                        size={1}
                                        spin={true}
                                        style={{ display: 'inline-flex', marginTop: '2px' }}
                                    />
                                ) : (
                                    'Search by User...'
                                )
                            }
                            value={currentValue().value ?? []}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
